import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../services/api';
import { formatDateDDMMYYYY, formatDateForInput, calculateAge } from '../utils/dateUtils';
import { validateTutorForm } from '../utils/validationUtils';
import { loadCheckboxState, saveCheckboxState } from '../utils/checkboxPersistence';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import ProfitSharingModal from './ProfitSharingModal';
import IcAssignmentModal from './IcAssignmentModal';
import '../assets/AdminTutors.css';

const AdminTutors = () => {
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTutor, setEditingTutor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    dateOfBirth: '',
    gender: '',
    race: '',
    phone: '',
    address: '',
    country: 'Singapore',
    linkedin: '',
    bio: '',
    highest: '',
    school: '',
    major: '',
    year: '',
    secondarySchool: '',
    secondarySubjects: '',
    secondaryGrades: ''
  });
  const [formError, setFormError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('code');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showInactive, setShowInactive] = useState(() => loadCheckboxState('admin_tutors', 'show_inactive', false));
  const [selectedTutors, setSelectedTutors] = useState([]);
  const [showFabMenu, setShowFabMenu] = useState(false);
  const [tutees, setTutees] = useState([]);
  const [showProfitSharingModal, setShowProfitSharingModal] = useState(false);
  // Combined Finance IC + Payment IC management/assignment modal (single view)
  const [showIcAssignmentModal, setShowIcAssignmentModal] = useState(false);

  useEffect(() => {
    fetchTutors();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close FAB menu if clicking outside
      if (!event.target.closest('.fab-container')) {
        setShowFabMenu(false);
      }
      
      // Clear selection if clicking outside table and FAB
      if (!event.target.closest('.tutors-table') && 
          !event.target.closest('.fab-container') &&
          !event.target.closest('.tutor-form-overlay') &&
          !event.target.closest('.search-bar') &&
          !event.target.closest('.controls-bar')) {
        if (selectedTutors.length > 0) {
          setSelectedTutors([]);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedTutors.length]);

  const fetchTutors = async () => {
    try {
      setLoading(true);
      const [tutorsResponse, tuteesResponse] = await Promise.all([
        adminApi.getTutors(),
        adminApi.getTutees()
      ]);
      setTutors(tutorsResponse.data);
      setTutees(tuteesResponse.data);
      setError('');
    } catch (err) {
      console.error('Error fetching tutors:', err);
      setError(`Failed to fetch tutors: ${err.response?.data?.message || err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const getTutorStats = (tutorId) => {
    const assignedTutees = tutees.filter(tutee =>
      tutee.subjects?.some(subject =>
        subject.assignedTutors?.some(at => {
          const tid = typeof at === 'object' ? (at.tutor?._id || at.tutor) : at;
          return tid === tutorId;
        })
      )
    );
    
    const activeSubjects = assignedTutees.reduce((count, tutee) => {
      const tutorSubjects = tutee.subjects?.filter(subject =>
        subject.isActive && subject.assignedTutors?.some(at => {
          const tid = typeof at === 'object' ? (at.tutor?._id || at.tutor) : at;
          return tid === tutorId;
        })
      );
      return count + (tutorSubjects?.length || 0);
    }, 0);
    
    return {
      tuteeCount: assignedTutees.length,
      activeSubjects
    };
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = {
      ...formData,
      [name]: value
    };
    
    // If dateOfBirth is changed, calculate and update age
    if (name === 'dateOfBirth') {
      updatedFormData.age = calculateAge(value);
    }
    
    setFormData(updatedFormData);
    
    // Clear form error when user starts typing
    if (formError) {
      setFormError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setFormError('');
    
    // Validate form data
    const validation = validateTutorForm(formData);
    
    if (!validation.isValid) {
      // Show all validation errors
      const errorMessages = Object.values(validation.errors).join('. ');
      setFormError(errorMessages);
      return;
    }
    
    try {
      if (editingTutor) {
        // Update existing tutor
        const updateData = { ...formData };
        delete updateData.password; // Don't send password on update
        await adminApi.updateTutor(editingTutor._id, updateData);
        setSuccess('Tutor updated successfully!');
      } else {
        // Create new tutor with default password and education history
        const { highest, school, major, year, secondarySchool, secondarySubjects, secondaryGrades, ...cleanFormData } = formData;
        
        const tutorData = {
          ...cleanFormData,
          // No password sent — backend uses DEFAULT_TUTOR_PASSWORD from env
          contact: {
            phone: formData.phone || '',
            address: formData.address || ''
          }
        };
        
        // If education details are provided, add to educationHistory
        if (highest && school) {
          tutorData.educationHistory = [{
            level: highest,
            institution: school,
            major: major || '',
            yearGraduated: year ? parseInt(year) : null,
            subjects: '',
            grades: ''
          }];
          console.log('📤 Sending tutor with education:', tutorData.educationHistory);
        }
        
        console.log('📤 Full tutor data being sent:', tutorData);
        await adminApi.createTutor(tutorData);
        setSuccess('Tutor created successfully! They will be required to change their password on first login.');
      }
      
      resetForm();
      fetchTutors();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      setFormError(errorMessage);
    }
  };

  const handleEdit = (tutor) => {
    setEditingTutor(tutor);
    setFormData({
      name: tutor.name || '',
      email: tutor.email || '',
      password: '',
      dateOfBirth: tutor.dateOfBirth || '',
      gender: tutor.gender || '',
      race: tutor.race || '',
      phone: tutor.contact?.phone || '',
      address: tutor.contact?.address || '',
      country: tutor.country || 'Singapore',
      linkedin: tutor.linkedin || '',
      bio: tutor.bio || '',
      highest: tutor.education?.highest || '',
      school: tutor.education?.school || '',
      major: tutor.education?.major || '',
      secondarySchool: tutor.education?.secondarySchool || '',
      secondarySubjects: tutor.education?.secondarySubjects || '',
      secondaryGrades: tutor.education?.secondaryGrades || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      dateOfBirth: '',
      gender: '',
      race: '',
      phone: '',
      address: '',
      country: 'Singapore',
      linkedin: '',
      bio: '',
      highest: '',
      school: '',
      major: '',
      year: '',
      secondarySchool: '',
      secondarySubjects: '',
      secondaryGrades: ''
    });
    setEditingTutor(null);
    setShowForm(false);
    setFormError('');
  };

  const handleResetPassword = async (tutorId, tutorName) => {
    if (window.confirm(`Reset password for ${tutorName}? Password will be reset to the default.`)) {
      try {
        await adminApi.bulkResetTutorPasswords([tutorId]);
        setSuccess(`Password reset successfully for ${tutorName}! Default password has been set.`);
        setTimeout(() => setSuccess(''), 5000);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
        setError(`Failed to reset password: ${errorMessage}`);
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleArchive = async (tutorId, tutorName) => {
    if (window.confirm(`Archive tutor ${tutorName}? They will be marked as inactive but data will be preserved.`)) {
      try {
        await adminApi.deleteTutor(tutorId);
        setSuccess('Tutor archived successfully! Enable "Show inactive" to view archived tutors.');
        await fetchTutors();
        setTimeout(() => setSuccess(''), 5000);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
        setError(`Failed to archive tutor: ${errorMessage}`);
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleActivate = async (tutorId, tutorName) => {
    if (window.confirm(`Reactivate ${tutorName}?`)) {
      try {
        await adminApi.updateTutor(tutorId, { isActive: true });
        setSuccess('Tutor reactivated successfully!');
        await fetchTutors();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
        setError(`Failed to reactivate tutor: ${errorMessage}`);
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleDelete = async (tutorId, tutorName) => {
    if (window.confirm(`⚠️ PERMANENTLY DELETE tutor ${tutorName}? This action cannot be undone and will remove all data.`)) {
      if (window.confirm(`Are you absolutely sure? Type the tutor's name to confirm deletion.`)) {
        try {
          await adminApi.permanentlyDeleteTutor(tutorId);
          setSuccess('Tutor permanently deleted!');
          await fetchTutors();
          setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
          const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
          setError(`Failed to delete tutor: ${errorMessage}`);
          setTimeout(() => setError(''), 3000);
        }
      }
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleSelectTutor = (tutorId) => {
    setSelectedTutors(prev => 
      prev.includes(tutorId)
        ? prev.filter(id => id !== tutorId)
        : [...prev, tutorId]
    );
  };

  const toggleSelectAll = () => {
    const filteredTutors = getFilteredAndSortedTutors();
    if (selectedTutors.length === filteredTutors.length) {
      setSelectedTutors([]);
    } else {
      setSelectedTutors(filteredTutors.map(t => t._id));
    }
  };

  const handleBulkArchive = async () => {
    if (selectedTutors.length === 0) return;
    
    if (window.confirm(`Archive ${selectedTutors.length} tutor(s)? They will be marked as inactive but data will be preserved.`)) {
      try {
        await adminApi.bulkArchiveTutors(selectedTutors);
        setSuccess(`${selectedTutors.length} tutor(s) archived successfully!`);
        setSelectedTutors([]);
        await fetchTutors();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
        setError(`Failed to archive tutors: ${errorMessage}`);
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleBulkUnarchive = async () => {
    if (selectedTutors.length === 0) return;
    
    if (window.confirm(`Reactivate ${selectedTutors.length} tutor(s)?`)) {
      try {
        await adminApi.bulkUnarchiveTutors(selectedTutors);
        setSuccess(`${selectedTutors.length} tutor(s) reactivated successfully!`);
        setSelectedTutors([]);
        await fetchTutors();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
        setError(`Failed to reactivate tutors: ${errorMessage}`);
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleBulkResetPasswords = async () => {
    if (selectedTutors.length === 0) return;
    
    if (window.confirm(`Reset password for ${selectedTutors.length} tutor(s)? Passwords will be reset to the default.`)) {
      try {
        await adminApi.bulkResetTutorPasswords(selectedTutors);
        setSuccess(`${selectedTutors.length} tutor password(s) reset successfully! Default password has been set.`);
        setSelectedTutors([]);
        await fetchTutors();
        setTimeout(() => setSuccess(''), 5000);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
        setError(`Failed to reset passwords: ${errorMessage}`);
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const viewTutorDetails = (tutorId) => {
    navigate(`/admin/tutors/${tutorId}`);
  };

  // Filter and sort tutors
  const getFilteredAndSortedTutors = () => {
    let filtered = tutors.filter(tutor => {
      // Filter by active/inactive - treat undefined as active (default)
      const isActive = tutor.isActive !== false;
      if (!showInactive && !isActive) return false;
      
      // Filter by search term
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          tutor.name.toLowerCase().includes(search) ||
          tutor.email.toLowerCase().includes(search) ||
          (tutor.contact?.phone && tutor.contact.phone.includes(search))
        );
      }
      return true;
    });

    // Sort by the selected field
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch(sortField) {
        case 'code':
          // Natural sorting for codes: handles formats like T2, T10, etc.
          const aCode = a.code || '';
          const bCode = b.code || '';
          
          // Try to extract letter and number: T10 -> ['T', '10']
          const aMatch = aCode.match(/^([A-Z]+)(\d+)$/);
          const bMatch = bCode.match(/^([A-Z]+)(\d+)$/);
          
          if (aMatch && bMatch) {
            // Compare letter prefix (T, A, B, etc.)
            if (aMatch[1] !== bMatch[1]) {
              return aMatch[1] < bMatch[1] ? (sortDirection === 'asc' ? -1 : 1) : (sortDirection === 'asc' ? 1 : -1);
            }
            // Compare number numerically (2 vs 10)
            const aNum = parseInt(aMatch[2], 10);
            const bNum = parseInt(bMatch[2], 10);
            return aNum < bNum ? (sortDirection === 'asc' ? -1 : 1) : (sortDirection === 'asc' ? 1 : -1);
          }
          
          // Fallback to string comparison if format doesn't match
          aVal = aCode;
          bVal = bCode;
          break;
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'email':
          aVal = a.email.toLowerCase();
          bVal = b.email.toLowerCase();
          break;
        case 'phone':
          aVal = a.contact?.phone || '';
          bVal = b.contact?.phone || '';
          break;
        case 'education':
          // Sort by latest education level
          const aLatestEdu = a.educationHistory?.length > 0
            ? a.educationHistory.reduce((latest, edu) => 
                (!latest || (edu.yearGraduated > latest.yearGraduated)) ? edu : latest
              )
            : null;
          const bLatestEdu = b.educationHistory?.length > 0
            ? b.educationHistory.reduce((latest, edu) => 
                (!latest || (edu.yearGraduated > latest.yearGraduated)) ? edu : latest
              )
            : null;
          aVal = aLatestEdu?.level?.toLowerCase() || a.education?.highest?.toLowerCase() || '';
          bVal = bLatestEdu?.level?.toLowerCase() || b.education?.highest?.toLowerCase() || '';
          break;
        case 'status':
          aVal = a.isActive !== false ? 1 : 0;
          bVal = b.isActive !== false ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };



  if (loading) return <LoadingSpinner message="Loading tutors..." />;
  if (error) return <ErrorMessage message={error} onRetry={fetchTutors} />;

  const filteredTutors = getFilteredAndSortedTutors();

  return (
    <div className="admin-tutors">
      <div className="page-header">
        <div className="header-title">
          <h1>Tutor Management</h1>
          <p className="subtitle">{tutors.length} total tutors • {tutors.filter(t => t.isActive !== false).length} active</p>
        </div>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="controls-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="controls-right">
          <button
            className="btn-settings"
            onClick={() => setShowIcAssignmentModal(true)}
            title="Assign Finance IC & Payment IC to tutees"
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" fill="currentColor"/>
            </svg>
            <span>Manage IC Assignments</span>
          </button>
          <button 
            className="btn-settings" 
            onClick={() => setShowProfitSharingModal(true)}
            title="Manage profit sharing allocations"
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" fill="currentColor"/>
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" fill="currentColor"/>
            </svg>
            <span>Manage Profit Sharing</span>
          </button>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => {
                const checked = e.target.checked;
                setShowInactive(checked);
                saveCheckboxState('admin_tutors', 'show_inactive', checked);
              }}
            />
            Show inactive
          </label>
          <button className="btn-add-icon" onClick={() => setShowForm(!showForm)} title="Add New Tutor">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>
      
      {showForm && (
        <div className="tutor-form-overlay" onClick={resetForm}>
          <div className="tutor-form-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>{editingTutor ? 'Edit Tutor' : 'Add New Tutor'}</h3>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter full name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="tutor@example.com"
                  />
                </div>
              </div>

              {!editingTutor && (
                <div className="form-note">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M8 4v4M8 10h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <span>A default password will be set. Tutor must change it on first login.</span>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dateOfBirth">Date of Birth (Age)</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="gender">Gender</label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="race">Race</label>
                  <input
                    type="text"
                    id="race"
                    name="race"
                    value={formData.race}
                    onChange={handleInputChange}
                    placeholder="e.g., Chinese, Malay, Indian"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+65 1234 5678"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="address">Address</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Full address"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="country">Country</label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Singapore"
                  />
                </div>
              </div>

              <div className="form-section">
                <h4>Education (Basic Info)</h4>
                <p className="form-hint">Add detailed education history after creating the tutor profile.</p>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="highest">Highest Education Level</label>
                    <select
                      id="highest"
                      name="highest"
                      value={formData.highest}
                      onChange={handleInputChange}
                    >
                      <option value="">Select level</option>
                      <option value="GCE 'O' Level">GCE 'O' Level</option>
                      <option value="GCE 'A' Level">GCE 'A' Level</option>
                      <option value="IB Diploma">IB Diploma</option>
                      <option value="Diploma">Diploma</option>
                      <option value="Bachelor's">Bachelor's Degree</option>
                      <option value="Master's">Master's Degree</option>
                      <option value="PhD">PhD</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="year">Year Graduated</label>
                    <input
                      type="number"
                      id="year"
                      name="year"
                      value={formData.year}
                      onChange={handleInputChange}
                      placeholder="e.g., 2020"
                      min="1950"
                      max={new Date().getFullYear() + 10}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="school">University/Institution</label>
                    <input
                      type="text"
                      id="school"
                      name="school"
                      value={formData.school}
                      onChange={handleInputChange}
                      placeholder="e.g., NUS, NTU, Raffles Institution"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="major">Major/Field of Study</label>
                    <input
                      type="text"
                      id="major"
                      name="major"
                      value={formData.major}
                      onChange={handleInputChange}
                      placeholder="e.g., Mathematics, Science Stream"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Additional Information</h4>
                <div className="form-group">
                  <label htmlFor="linkedin">LinkedIn Profile</label>
                  <input
                    type="url"
                    id="linkedin"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleInputChange}
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="bio">Bio</label>
                  <textarea
                    id="bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Brief description about the tutor..."
                  />
                </div>
              </div>
              
              {formError && <div className="form-error">{formError}</div>}
              
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingTutor ? 'Update Tutor' : 'Create Tutor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      <div className="tutors-list">
        {filteredTutors.length === 0 ? (
          <div className="no-data">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="30" stroke="#e0e0e0" strokeWidth="2"/>
              <path d="M32 20v24M20 32h24" stroke="#e0e0e0" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p>{searchTerm ? 'No tutors match your search.' : 'No tutors found. Click "Add Tutor" to get started.'}</p>
          </div>
        ) : (
          <div className="tutors-table-container">
            <table className="tutors-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedTutors.length === filteredTutors.length && filteredTutors.length > 0}
                      onChange={toggleSelectAll}
                      title="Select all"
                    />
                  </th>
                  <th onClick={() => handleSort('name')} className="sortable">
                    <div className="th-content">
                      Name
                      {sortField === 'name' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('code')} className="sortable">
                    <div className="th-content">
                      Code
                      {sortField === 'code' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('email')} className="sortable">
                    <div className="th-content">
                      Email
                      {sortField === 'email' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('education')} className="sortable">
                    <div className="th-content">
                      Latest Education
                      {sortField === 'education' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('phone')} className="sortable">
                    <div className="th-content">
                      Phone
                      {sortField === 'phone' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="text-center">
                    <div className="th-content">
                      Assignments
                    </div>
                  </th>
                  <th onClick={() => handleSort('status')} className="sortable">
                    <div className="th-content">
                      Status
                      {sortField === 'status' && (
                        <span className="sort-icon">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTutors.map((tutor) => (
                  <tr key={tutor._id} onClick={() => viewTutorDetails(tutor._id)} className="clickable-row">
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedTutors.includes(tutor._id)}
                        onChange={() => toggleSelectTutor(tutor._id)}
                      />
                    </td>
                    <td>
                      <div className="tutor-info">
                        <div className="tutor-avatar">
                          {tutor.profileImage
                            ? <img src={tutor.profileImage} alt={tutor.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                            : tutor.name.charAt(0).toUpperCase()
                          }
                        </div>
                        <div>
                          <div className="tutor-name">{tutor.name}</div>
                          {(tutor.dateOfBirth || tutor.age) && (
                            <div className="tutor-meta">
                              {tutor.dateOfBirth ? (
                                `Age ${calculateAge(tutor.dateOfBirth)}`
                              ) : (
                                `Age ${tutor.age}`
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {tutor.code ? (
                        <span className="tutor-code">{tutor.code}</span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{tutor.email}</td>
                    <td>
                      {(() => {
                        // Get the latest education from educationHistory (highest year)
                        const latestEdu = tutor.educationHistory?.length > 0
                          ? tutor.educationHistory.reduce((latest, edu) => 
                              (!latest || (edu.yearGraduated > latest.yearGraduated)) ? edu : latest
                            )
                          : null;
                        
                        if (latestEdu) {
                          return (
                            <div className="education-info">
                              <div className="education-level">
                                {latestEdu.level} {latestEdu.yearGraduated && `(${latestEdu.yearGraduated})`}
                              </div>
                              {latestEdu.institution && <div className="education-school">{latestEdu.institution}</div>}
                            </div>
                          );
                        } else if (tutor.education?.highest) {
                          return (
                            <div className="education-info">
                              <div className="education-level">{tutor.education.highest}</div>
                              {tutor.education.school && <div className="education-school">{tutor.education.school}</div>}
                            </div>
                          );
                        } else {
                          return <span className="text-muted">-</span>;
                        }
                      })()}
                    </td>
                    <td>{tutor.contact?.phone || <span className="text-muted">-</span>}</td>
                    <td className="text-center">
                      {(() => {
                        const stats = getTutorStats(tutor._id);
                        if (stats.tuteeCount === 0 && stats.activeSubjects === 0) {
                          return <span className="text-muted">-</span>;
                        }
                        return (
                          <div className="assignments-cell">
                            <span className="stat-badge tutee-count" title="Tutees">
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M6 6a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM2 11a4 4 0 0 1 8 0" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                              </svg>
                              {stats.tuteeCount}
                            </span>
                            <span className="stat-divider">•</span>
                            <span className="stat-badge lessons-count" title="Active Lessons">
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M2 3h8M2 6h8M2 9h5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                              </svg>
                              {stats.activeSubjects}
                            </span>
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ color: tutor.isActive !== false ? '#10b981' : '#dc2626', fontWeight: 'bold', fontSize: '14px' }}>
                      {tutor.isActive !== false ? '● Active' : '● Inactive'}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="action-buttons">
                        <button 
                          className="btn-action btn-view"
                          onClick={(e) => {
                            e.stopPropagation();
                            viewTutorDetails(tutor._id);
                          }}
                          title="View details"
                        >
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3C4.5 3 1.7 5.6 1 8c.7 2.4 3.5 5 7 5s6.3-2.6 7-5c-.7-2.4-3.5-5-7-5z" stroke="currentColor" strokeWidth="1.5"/>
                            <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/>
                          </svg>
                        </button>
                        {tutor.isActive !== false ? (
                          <button 
                            className="btn-action btn-archive"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchive(tutor._id, tutor.name);
                            }}
                            title="Archive (deactivate)"
                          >
                            <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                              <path d="M2 4h12M3 4v9a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4M6 7v4M10 7v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          </button>
                        ) : (
                          <>
                            <button 
                              className="btn-action btn-activate"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleActivate(tutor._id, tutor.name);
                              }}
                              title="Reactivate"
                            >
                              <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                                <path d="M8 2v4M8 10v4M2 8h4M10 8h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                            </button>
                            <button 
                              className="btn-action btn-delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(tutor._id, tutor.name);
                              }}
                              title="Delete permanently"
                            >
                              <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                                <path d="M2 4h12M5 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1M13 4v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4h10z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedTutors.length > 0 && (
        <div className="fab-container">
          <div className={`fab-menu ${showFabMenu ? 'open' : ''}`}>
            <button className="fab-menu-item" onClick={() => { handleBulkResetPasswords(); setShowFabMenu(false); }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2v4M10 10v4M3 10h4M13 10h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Reset Passwords</span>
            </button>
            <button className="fab-menu-item" onClick={() => { handleBulkArchive(); setShowFabMenu(false); }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 6h14M5 6v10a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6M8 9v4M12 9v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Archive</span>
            </button>
            <button className="fab-menu-item" onClick={() => { handleBulkUnarchive(); setShowFabMenu(false); }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 3v6M10 13v4M3 10h6M13 10h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Unarchive</span>
            </button>
            <button className="fab-menu-item clear" onClick={() => { setSelectedTutors([]); setShowFabMenu(false); }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 5l10 10M15 5l-10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Clear</span>
            </button>
          </div>
          <button 
            className={`fab-button ${showFabMenu ? 'open' : ''}`}
            onClick={() => setShowFabMenu(!showFabMenu)}
            title={`${selectedTutors.length} tutor${selectedTutors.length > 1 ? 's' : ''} selected`}
          >
            <div className="fab-count">{selectedTutors.length}</div>
            <svg className="fab-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* Profit Sharing Modal */}
      <ProfitSharingModal
        isOpen={showProfitSharingModal}
        onClose={() => setShowProfitSharingModal(false)}
        profitSharers={tutors.filter(t => t.isCoFounder && t.isActive)}
        mode="admin"
      />

      {/* Combined Finance IC + Payment IC assignment (single-view list) */}
      <IcAssignmentModal
        isOpen={showIcAssignmentModal}
        onClose={() => setShowIcAssignmentModal(false)}
      />
    </div>
  );
};

export default AdminTutors;